import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { updateVariety, uploadFile, addVarietyPriceToVariety, removeVarietyPrice, updateVarietyPrice, markPriceAsSold, markPriceAsAvailable } from '../api';
import { normalizeShortBarcode, sanitizeShortBarcodeInput } from '../utils/barcode';
import { CLIENT_TYPE_OPTIONS, resolvePriceClientType, getClientTypeLabel } from '../utils/clientType';
import { FiEdit, FiTrash2, FiUpload } from 'react-icons/fi';
import { notify } from '../utils/alertBus';

const MAX_VARIETY_NAME_LENGTH = 120;
const MAX_VARIETY_DESCRIPTION_LENGTH = 30;

const extractApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || fallbackMessage;

const PriceItem = ({
  id,
  name,
  prices,
  description,
  productImage,
  priceAutoRuleOverride,
  barcodeScope,
  allVarieties,
  lastStockItem,
  missingUpdate,
  onDeleteVariety,
  onVarietyUpdate,
  canUpdateProduct = true,
  hideCard = false,
  forceOpen,
  onRequestClose,
  defaultEditOpen = false,
  hideDeleteVariety = false,
  modalZIndexClass = 'z-30',
  onUpdateSuccess,
}) => {
  const [showEdit, setShowEdit] = useState(!!defaultEditOpen);
  const [nameInput, setNameInput] = useState(name);
  const [descriptionInput, setDescriptionInput] = useState(description || '');
  const [productImageInput, setProductImageInput] = useState(productImage);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [priceSoldOutLoading, setPriceSoldOutLoading] = useState({});
  const [isMissingUpdate, setIsMissingUpdate] = useState(missingUpdate);
  const [productPricesInputs, setProductPricesInputs] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [priceErrors, setPriceErrors] = useState({});
  const [inheritGlobalRule, setInheritGlobalRule] = useState(!priceAutoRuleOverride);
  const [priceRuleMode, setPriceRuleMode] = useState(priceAutoRuleOverride?.mode === 'subtotal' ? 'subtotal' : 'quantity');
  const [priceRuleThresholds, setPriceRuleThresholds] = useState({
    detalle: priceAutoRuleOverride?.thresholds?.detalle ?? '',
    mayorista: priceAutoRuleOverride?.thresholds?.mayorista ?? '',
    importador: priceAutoRuleOverride?.thresholds?.importador ?? '',
  });
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');

  const [showModalState, setShowModalState] = useState(false);

  const isControlled = typeof forceOpen === 'boolean';
  const showModal = isControlled ? forceOpen : showModalState;

  const onClose = () => {
    if (isControlled) {
      onRequestClose?.();
      return;
    }
    setShowModalState(false);
  };
  const onOpen = () => {
    if (!isControlled) {
      setShowModalState(true);
    }
  };

  const fileInputs = useRef([]);

  useEffect(() => {
    if (showEdit) return;
    setNameInput(name);
    setDescriptionInput(description);
    setProductImageInput(productImage);
  }, [name, description, productImage, showEdit]);

  useEffect(() => {
    if (showEdit) return;
    setInheritGlobalRule(!priceAutoRuleOverride);
    setPriceRuleMode(priceAutoRuleOverride?.mode === 'subtotal' ? 'subtotal' : 'quantity');
    setPriceRuleThresholds({
      detalle: priceAutoRuleOverride?.thresholds?.detalle ?? '',
      mayorista: priceAutoRuleOverride?.thresholds?.mayorista ?? '',
      importador: priceAutoRuleOverride?.thresholds?.importador ?? '',
    });
  }, [priceAutoRuleOverride, showEdit]);

  useEffect(() => {
    if (!canUpdateProduct && showEdit) {
      setShowEdit(false);
    }
  }, [canUpdateProduct, showEdit]);

  useEffect(() => {
    if (showModal) {
      setShowEdit(defaultEditOpen && canUpdateProduct);
      setFormErrors({});
      setPriceErrors({});
    }
  }, [showModal, defaultEditOpen, canUpdateProduct]);

  useEffect(() => {
    if (!showEdit) {
      setFormErrors({});
      setPriceErrors({});
    }
  }, [showEdit]);

  useEffect(() => {
    if (showEdit) return;
    const nextRows = (prices || []).map((price) => ({
      id: price.id,
      price: price.price ?? '',
      metricUnit: price.metricUnit,
      priceImage: price.priceImage,
      description: price.description || '',
      clientType: resolvePriceClientType(price),
      barcode: price.barcode ?? '',
      useScaleWeight: Boolean(price.useScaleWeight),
      isSoldOut: price.isSoldOut ?? false,
    }));
    setProductPricesInputs(nextRows);
  }, [prices, showEdit]);

  const buildVarietyPrices = (rows) =>
    rows.map(({ file, ...rest }) => ({
      ...rest,
      price: rest.price === "" ? null : rest.price,
    }));

  const effectiveBarcodeScope = barcodeScope === 'per_variety' ? 'per_variety' : 'global';
  const varietyNameMap = useMemo(() => {
    const map = new Map();
    (allVarieties || []).forEach((variety) => {
      if (variety?.id) {
        map.set(variety.id, variety.varietyName || `Variedad ${variety.id}`);
      }
    });
    return map;
  }, [allVarieties]);
  const originalPriceById = useMemo(() => {
    const map = new Map();
    (prices || []).forEach((price) => {
      if (price?.id) {
        map.set(Number(price.id), {
          barcode: normalizeShortBarcode(price.barcode),
          clientType: resolvePriceClientType(price),
        });
      }
    });
    return map;
  }, [prices]);

  const barcodeEntries = useMemo(() => {
    const entries = [];
    const currentVarietyId = id;
    productPricesInputs.forEach((price, index) => {
      const barcode = normalizeShortBarcode(price.barcode);
      if (!barcode) return;
      entries.push({
        key: price.id ? `id-${price.id}` : `draft-${index}`,
        varietyId: currentVarietyId,
        barcode,
        clientType: resolvePriceClientType(price),
      });
    });
    (allVarieties || []).forEach((variety) => {
      if (!variety || variety.id === currentVarietyId) return;
      (variety.VarietyPrices || []).forEach((price) => {
        const barcode = normalizeShortBarcode(price.barcode);
        if (!barcode) return;
        entries.push({
          key: price.id ? `id-${price.id}` : `variety-${variety.id}`,
          varietyId: variety.id,
          barcode,
          clientType: resolvePriceClientType(price),
        });
      });
    });
    return entries;
  }, [productPricesInputs, allVarieties, id]);

  const getBarcodeWarning = (price, index) => {
    const barcode = normalizeShortBarcode(price.barcode);
    if (!barcode) return null;
    const clientType = resolvePriceClientType(price);
    const currentKey = price.id ? `id-${price.id}` : `draft-${index}`;
    const matches = barcodeEntries.filter((entry) => {
      if (entry.key === currentKey) return false;
      if (entry.barcode !== barcode) return false;
      if (entry.clientType !== clientType) return false;
      if (effectiveBarcodeScope === 'per_variety') {
        return entry.varietyId === id;
      }
      return true;
    });
    if (!matches.length) return null;
    const label = getClientTypeLabel(clientType);
    if (effectiveBarcodeScope === 'per_variety') {
      return `Código de barras ya existe para ${label} en esta variedad.`;
    }
    const varietyNames = matches
      .map((entry) => varietyNameMap.get(entry.varietyId))
      .filter(Boolean);
    const uniqueNames = Array.from(new Set(varietyNames));
    if (uniqueNames.length) {
      return `Código de barras ya existe para ${label} en: ${uniqueNames.join(', ')}.`;
    }
    return `Código de barras ya existe para ${label} en otra variedad.`;
  };
  const isBarcodeIdentityChanged = (price) => {
    if (!price?.id) return true;
    const original = originalPriceById.get(Number(price.id));
    if (!original) return true;
    const nextBarcode = normalizeShortBarcode(price.barcode);
    const nextClientType = resolvePriceClientType(price);
    return original.barcode !== nextBarcode || original.clientType !== nextClientType;
  };

  const validateRuleThresholds = (nextErrors) => {
    if (inheritGlobalRule) return true;
    const allowedTypes = new Set(CLIENT_TYPE_OPTIONS.map((option) => option.value));
    let hasAtLeastOneThreshold = false;
    let isValid = true;
    const nextThresholdErrors = {};

    Object.entries(priceRuleThresholds || {}).forEach(([type, rawValue]) => {
      if (!allowedTypes.has(type)) return;
      const value = String(rawValue || '').trim();
      if (!value) return;
      hasAtLeastOneThreshold = true;
      const numeric = Number(value);
      if (!Number.isInteger(numeric) || numeric <= 0) {
        nextThresholdErrors[type] = 'Debe ser un entero mayor a 0.';
        isValid = false;
      }
    });

    if (!hasAtLeastOneThreshold) {
      nextErrors.ruleThresholds = 'Debes definir al menos un umbral para usar una regla personalizada.';
      isValid = false;
    }

    if (Object.keys(nextThresholdErrors).length > 0) {
      nextErrors.thresholds = nextThresholdErrors;
    }

    return isValid;
  };

  const validateVarietyForm = (rows) => {
    const normalizedName = String(nameInput || '').trim();
    const normalizedDescription = String(descriptionInput || '').trim();
    const nextFormErrors = {};
    const nextPriceErrors = {};
    let isValid = true;

    if (!normalizedName) {
      nextFormErrors.varietyName = 'El nombre de la variedad es obligatorio.';
      isValid = false;
    } else if (normalizedName.length > MAX_VARIETY_NAME_LENGTH) {
      nextFormErrors.varietyName = `Máximo ${MAX_VARIETY_NAME_LENGTH} caracteres.`;
      isValid = false;
    }

    if (normalizedDescription.length > MAX_VARIETY_DESCRIPTION_LENGTH) {
      nextFormErrors.description = `Máximo ${MAX_VARIETY_DESCRIPTION_LENGTH} caracteres.`;
      isValid = false;
    }

    rows.forEach((price, index) => {
      const rowErrors = {};
      const metricUnit = String(price.metricUnit || '').trim();
      const rawPrice = String(price.price ?? '').trim();
      const normalizedBarcode = normalizeShortBarcode(price.barcode);
      const normalizedDescriptionPrice = String(price.description || '').trim();

      if (!metricUnit) {
        rowErrors.metricUnit = 'La unidad es obligatoria.';
      }

      if (!price.id && rawPrice === '') {
        rowErrors.price = 'El precio es obligatorio para nuevos precios.';
      } else if (rawPrice !== '') {
        const numericPrice = Number(rawPrice);
        if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
          rowErrors.price = 'Debe ser un número mayor a 0.';
        }
      }

      if (!CLIENT_TYPE_OPTIONS.some((option) => option.value === price.clientType)) {
        rowErrors.clientType = 'Tipo de cliente inválido.';
      }

      if (normalizedDescriptionPrice.length > MAX_VARIETY_DESCRIPTION_LENGTH) {
        rowErrors.description = `Máximo ${MAX_VARIETY_DESCRIPTION_LENGTH} caracteres.`;
      }

      if (normalizedBarcode && !/^\d{1,7}$/.test(normalizedBarcode)) {
        rowErrors.barcode = 'Código inválido. Usa hasta 7 dígitos.';
      } else {
        const warning = getBarcodeWarning(price, index);
        if (warning && isBarcodeIdentityChanged(price)) {
          rowErrors.barcode = warning;
        }
      }

      if (Object.keys(rowErrors).length > 0) {
        nextPriceErrors[index] = rowErrors;
        isValid = false;
      }
    });

    if (!validateRuleThresholds(nextFormErrors)) {
      isValid = false;
    }

    setFormErrors(nextFormErrors);
    setPriceErrors(nextPriceErrors);
    return {
      isValid,
      normalizedName,
      normalizedDescription,
    };
  };

  const clearFormError = (field) => {
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const clearPriceError = (index, field) => {
    setPriceErrors((prev) => {
      const row = prev?.[index];
      if (!row || !row[field]) return prev;
      const next = { ...prev };
      const rowNext = { ...row };
      delete rowNext[field];
      if (Object.keys(rowNext).length === 0) {
        delete next[index];
      } else {
        next[index] = rowNext;
      }
      return next;
    });
  };

  const sellablePrices = productPricesInputs.filter(
    (price) => price.price !== null && price.price !== undefined && price.price !== ""
  );
  const numericPrices = sellablePrices
    .map((price) => Number(price.price))
    .filter((value) => Number.isFinite(value));
  const lowestPrice = numericPrices.length ? Math.min(...numericPrices) : null;
  const highestPrice = numericPrices.length ? Math.max(...numericPrices) : null;
  const lowestPriceUnit = sellablePrices.find((price) => Number(price.price) === lowestPrice)?.metricUnit || '';
  const highestPriceUnit = sellablePrices.find((price) => Number(price.price) === highestPrice)?.metricUnit || '';
  const isVarietySoldOut =
    productPricesInputs.length > 0 && productPricesInputs.every((price) => price.isSoldOut);

  // Delete Price
  const handleDeletePrice = async (priceId, index) => {
    if (!canUpdateProduct) return;
    if (!priceId) {
      setProductPricesInputs(productPricesInputs.filter((_, i) => i !== index));
      return;
    }

    try {
      await removeVarietyPrice(branchId, priceId);
      setProductPricesInputs(productPricesInputs.filter((price) => price.id !== priceId));
    } catch (error) {
      console.error("Error deleting product price:", error);
      notify(extractApiErrorMessage(error, 'No se pudo eliminar el precio.'));
    }
  };

  const handleAddPrice = () => {
    if (!canUpdateProduct) return;
    setProductPricesInputs([
      ...productPricesInputs,
      {
        price: "",
        metricUnit: "",
        priceImage: null,
        file: null,
        description: "",
        clientType: 'mayorista',
        barcode: "",
        useScaleWeight: false,
        isSoldOut: false,
      },
    ]);
  };

  const handleTogglePriceSoldOut = async (priceId, currentValue) => {
    if (!canUpdateProduct || !priceId) return;
    setPriceSoldOutLoading((prev) => ({ ...prev, [priceId]: true }));
    try {
      if (currentValue) {
        await markPriceAsAvailable(branchId, priceId);
      } else {
        await markPriceAsSold(branchId, priceId);
      }
      const updatedPrices = productPricesInputs.map((price) =>
        price.id === priceId ? { ...price, isSoldOut: !currentValue } : price
      );
      setProductPricesInputs(updatedPrices);
      const cleanedPrices = buildVarietyPrices(updatedPrices);
      const updatedVariety = {
        id,
        VarietyPrices: cleanedPrices,
        isSoldOut: cleanedPrices.length > 0 && cleanedPrices.every((price) => price.isSoldOut),
      };
      onVarietyUpdate?.(updatedVariety);
    } catch (error) {
      console.error("Error updating price sold-out:", error);
      notify(extractApiErrorMessage(error, 'No se pudo actualizar el estado del precio.'));
    } finally {
      setPriceSoldOutLoading((prev) => ({ ...prev, [priceId]: false }));
    }
  };

  const handleUpdateVariety = async () => {
    if (!canUpdateProduct) return;
    const updatedPrices = productPricesInputs.map((row) => ({ ...row }));
    const validation = validateVarietyForm(updatedPrices);
    if (!validation.isValid) {
      notify('Corrige los campos marcados antes de guardar.');
      return;
    }

    setIsLoading(true);
    setIsMissingUpdate(false);
    let uploadedImageUrl = productImageInput;

    try {
      if (file) {
        const response = await uploadFile(file);
        uploadedImageUrl = response?.url || uploadedImageUrl;
      }

      // Update product details
      const variety = await updateVariety(
        branchId,
        id, {
        varietyName: validation.normalizedName,
        description: validation.normalizedDescription,
        varietyImage: uploadedImageUrl,
        priceAutoRuleOverride: inheritGlobalRule
          ? null
          : {
            mode: priceRuleMode === 'subtotal' ? 'subtotal' : 'quantity',
            thresholds: {
              detalle: priceRuleThresholds.detalle === '' ? null : Number(priceRuleThresholds.detalle),
              mayorista: priceRuleThresholds.mayorista === '' ? null : Number(priceRuleThresholds.mayorista),
              importador: priceRuleThresholds.importador === '' ? null : Number(priceRuleThresholds.importador),
            },
          },
      });

      let varietyPrices = [];

      // ✅ Upload new images before updating prices
      for (let i = 0; i < updatedPrices.length; i++) {
        const price = updatedPrices[i];
        let priceImageUrl = price.priceImage;
        const metricUnit = String(price.metricUnit || '').trim();
        const descriptionValue = String(price.description || '').trim();
        const numericPrice = String(price.price ?? '').trim() === '' ? '' : Number(price.price);

        if (price.file) {
          // Upload new image if selected
          const imageResponse = await uploadFile(price.file);
          priceImageUrl = imageResponse?.url || priceImageUrl;
          updatedPrices[i].priceImage = priceImageUrl; // Update image URL in state
        }

        if (price.id) {
          const normalizedBarcode = normalizeShortBarcode(price.barcode);
          const normalizedClientType = resolvePriceClientType(price);
          const originalIdentity = originalPriceById.get(Number(price.id));
          const updatePayload = {
            metricUnit,
            priceImage: priceImageUrl,
            description: descriptionValue,
            useScaleWeight: Boolean(price.useScaleWeight),
          };
          if (!originalIdentity || originalIdentity.clientType !== normalizedClientType) {
            updatePayload.clientType = normalizedClientType;
          }
          if (!originalIdentity || originalIdentity.barcode !== normalizedBarcode) {
            updatePayload.barcode = normalizedBarcode;
          }
          if (numericPrice !== '') {
            updatePayload.price = numericPrice;
          }
          // Update existing ProductPrice
          const updatedVarietyPrice = await updateVarietyPrice(branchId, price.id, {
            ...updatePayload,
          });
          varietyPrices.push(updatedVarietyPrice);
        } else {
          // Add new ProductPrice
          const varietyPrice = await addVarietyPriceToVariety(branchId, {
            varietyId: id,
            price: numericPrice,
            metricUnit,
            priceImage: priceImageUrl,
            description: descriptionValue,
            clientType: price.clientType,
            barcode: normalizeShortBarcode(price.barcode),
            useScaleWeight: Boolean(price.useScaleWeight),
          });
          updatedPrices[i].id = varietyPrice.id; // Store new id in state
          varietyPrices.push(varietyPrice);
        }
      }

      variety.VarietyPrices = varietyPrices; // Update prices in variety object
      onVarietyUpdate?.(variety);

      // ✅ Ensure updated images are displayed after upload
      setProductPricesInputs(updatedPrices);
      setShowEdit(false);
      notify('Variedad actualizada correctamente.');
      onUpdateSuccess?.();
      onClose();

    } catch (error) {
      console.error("Error updating product and prices:", error);
      notify(extractApiErrorMessage(error, 'No se pudieron guardar los cambios de la variedad.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!hideCard && (
        <div
          key={id}
          className={`p-3 ${isMissingUpdate ? "bg-green-300" : "bg-white"} border rounded-lg shadow hover:shadow-md cursor-pointer flex flex-col justify-between`}
          style={{ minHeight: '130px' }}
          onClick={onOpen}
        >
          {/* Image + Name + Costo */}
          <div className="flex items-center gap-3 mb-2">
            <img
              src={productImageInput || 'https://placehold.co/200'}
              alt={nameInput}
              className="w-12 h-12 object-cover rounded-lg shadow"
            />
            <div className="flex flex-col flex-1">
              <h3 className="text-sm font-semibold break-words">
                {nameInput}
              </h3>
              {lastStockItem && (
                <span className="text-xs text-gray-500">Costo: ${lastStockItem.price}</span>
              )}
            </div>
          </div>

          {/* Prices or Sold Out */}
          <div className="flex justify-between text-sm text-gray-700 mb-2">
            {isVarietySoldOut ? (
              <span className="text-red-600 font-bold">AGOTADO</span>
            ) : (
              <>
                {numericPrices.length === 0 ? (
                  <span className="text-gray-500">Sin precio</span>
                ) : numericPrices.length === 1 ? (
                  <span>{sellablePrices[0].metricUnit}: ${numericPrices[0]}</span>
                ) : numericPrices.length > 1 ? (
                  <>
                    <span>
                      {lowestPriceUnit}: ${lowestPrice}
                    </span>
                    <span>
                      {highestPriceUnit}: ${highestPrice}
                    </span>
                  </>
                ): (null)}
              </>
            )}
          </div>

          {/* Bottom: Ver Detalles */}
          <p className="text-center font-bold text-sm">[Ver Detalles]</p>
        </div>
      )}


      {showModal && (
        <div
          className={`fixed inset-0 bg-black/50 flex items-start justify-center ${modalZIndexClass} px-4 py-6 overflow-y-auto`}
          onClick={onClose}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[92vh] sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50 shrink-0">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Variedad</p>
                <h2 className="text-lg font-semibold text-gray-900">{nameInput}</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {lastStockItem
                    ? `Última compra: ${new Date(lastStockItem.createdAt).toLocaleString()} · Costo: ${lastStockItem.price}`
                    : 'Sin registros de compra recientes'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!showEdit && canUpdateProduct && (
                  <button
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:text-gray-900"
                    onClick={() => setShowEdit(true)}
                    disabled={isLoading}
                  >
                    Editar variedad
                  </button>
                )}
                {!showEdit && canUpdateProduct && !hideDeleteVariety && !!onDeleteVariety && (
                  <button
                    className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
                    onClick={() => onDeleteVariety(id)}
                    disabled={isLoading}
                  >
                    Eliminar
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-4 sm:gap-6 overflow-y-auto min-h-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
                  <div className="text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Imagen</div>
                  <div className="relative mx-auto w-24 h-24 sm:w-32 sm:h-32 lg:w-full lg:aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                    <img
                      src={productImageInput || 'https://placehold.co/400'}
                      alt={name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  {showEdit && (
                    <div className="mt-3">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`variety-image-${id}`}
                        disabled={isLoading}
                        onChange={(e) => {
                          if (!e.target.files?.[0]) return;
                          setProductImageInput(URL.createObjectURL(e.target.files[0]) || productImageInput);
                          setFile(e.target.files[0]);
                        }}
                      />
                      <label
                        htmlFor={`variety-image-${id}`}
                        className="block text-center text-sm font-medium px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                      >
                        Cambiar imagen
                      </label>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                    {showEdit ? (
                      <input
                        className={`mt-2 border rounded-lg p-2 w-full ${
                          formErrors.varietyName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={nameInput}
                        maxLength={MAX_VARIETY_NAME_LENGTH}
                        disabled={isLoading}
                        onChange={(e) => {
                          setNameInput(e.target.value);
                          clearFormError('varietyName');
                        }}
                      />
                    ) : (
                      <p className="mt-2 text-sm text-gray-700">{nameInput}</p>
                    )}
                    {showEdit && formErrors.varietyName && (
                      <p className="mt-1 text-xs text-red-600">{formErrors.varietyName}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase">Descripción</label>
                    {showEdit ? (
                      <input
                        type="text"
                        className={`mt-2 border rounded-lg p-2 w-full ${
                          formErrors.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Descripción"
                        value={descriptionInput}
                        disabled={isLoading}
                        maxLength={MAX_VARIETY_DESCRIPTION_LENGTH}
                        onChange={(e) => {
                          setDescriptionInput(e.target.value || '');
                          clearFormError('description');
                        }}
                      />
                    ) : (
                      <p className="mt-2 text-sm text-gray-500">{descriptionInput || 'Sin descripción'}</p>
                    )}
                    {showEdit && formErrors.description && (
                      <p className="mt-1 text-xs text-red-600">{formErrors.description}</p>
                    )}
                  </div>
                  {isVarietySoldOut && (
                    <div className="inline-flex items-center rounded-full bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 w-fit">
                      AGOTADO
                    </div>
                  )}
                  <div className="pt-2 border-t border-gray-100 space-y-2">
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={inheritGlobalRule}
                        onChange={(e) => {
                          setInheritGlobalRule(e.target.checked);
                          clearFormError('ruleThresholds');
                          clearFormError('thresholds');
                        }}
                        disabled={!showEdit || isLoading}
                      />
                      Heredar regla global de selección automática
                    </label>
                    {!inheritGlobalRule && (
                      <div className="space-y-2">
                        <select
                          className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                          value={priceRuleMode}
                          onChange={(e) => setPriceRuleMode(e.target.value)}
                          disabled={!showEdit || isLoading}
                        >
                          <option value="quantity">Cantidad por variedad</option>
                          <option value="subtotal">Subtotal documento</option>
                        </select>
                        <div className="grid grid-cols-1 gap-2">
                          {['detalle', 'mayorista', 'importador'].map((type) => (
                            <input
                              key={type}
                              type="text"
                              inputMode="numeric"
                              className={`border rounded-lg p-2 text-sm ${
                                formErrors?.thresholds?.[type] ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder={`Umbral ${type}`}
                              value={priceRuleThresholds[type]}
                              onChange={(e) =>
                                {
                                  setPriceRuleThresholds((prev) => ({
                                    ...prev,
                                    [type]: String(e.target.value || '').replace(/[^\d]/g, ''),
                                  }));
                                  setFormErrors((prev) => {
                                    if (!prev?.thresholds?.[type] && !prev?.ruleThresholds) return prev;
                                    const next = { ...prev };
                                    if (next.ruleThresholds) {
                                      delete next.ruleThresholds;
                                    }
                                    if (next.thresholds?.[type]) {
                                      const thresholdErrors = { ...(next.thresholds || {}) };
                                      delete thresholdErrors[type];
                                      if (Object.keys(thresholdErrors).length === 0) {
                                        delete next.thresholds;
                                      } else {
                                        next.thresholds = thresholdErrors;
                                      }
                                    }
                                    return next;
                                  });
                                }
                              }
                              disabled={!showEdit || isLoading}
                            />
                          ))}
                        </div>
                        {formErrors.ruleThresholds && (
                          <p className="text-xs text-red-600">{formErrors.ruleThresholds}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Precios</h3>
                    <p className="text-xs text-gray-500">
                      Configura precios, barcode y estado por tipo de cliente.
                    </p>
                  </div>
                  {showEdit && (
                    <button
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                      onClick={handleAddPrice}
                      disabled={isLoading}
                    >
                      + Nuevo precio
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {productPricesInputs.length === 0 && (
                    <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500">
                      Aún no hay precios registrados.
                    </div>
                  )}

                  {productPricesInputs.map((price, index) => {
                    const barcodeWarning = showEdit ? getBarcodeWarning(price, index) : null;
                    return (
                      <div key={price.id ?? `draft-${index}`} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                        <div className="flex flex-col lg:flex-row gap-4">
                          <div className="flex items-center gap-3">
                            <input
                              type="file"
                              accept="image/*"
                              ref={(input) => (fileInputs.current[index] = input)}
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files.length > 0) {
                                  const file = e.target.files[0];
                                  const updatedPrices = [...productPricesInputs];
                                  updatedPrices[index].file = file;
                                  updatedPrices[index].priceImage = URL.createObjectURL(file);
                                  setProductPricesInputs(updatedPrices);
                                }
                              }}
                              disabled={!showEdit || isLoading}
                            />
                            <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                              <img
                                src={price.priceImage || productImageInput || 'https://placehold.co/80'}
                                alt="Precio"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            {showEdit && (
                              <button
                                className="px-2 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 hover:text-gray-800"
                                onClick={() => fileInputs.current[index]?.click()}
                                disabled={isLoading}
                              >
                                Cambiar imagen
                              </button>
                            )}
                          </div>

                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-gray-600 uppercase">Precio</label>
                              {showEdit ? (
                                <input
                                  className={`mt-2 border rounded-lg p-2 w-full ${
                                    priceErrors?.[index]?.price ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  type="number"
                                  min="0"
                                  value={price.price}
                                  disabled={isLoading}
                                  onChange={(e) => {
                                    const updatedPrices = [...productPricesInputs];
                                    updatedPrices[index].price = e.target.value;
                                    setProductPricesInputs(updatedPrices);
                                    clearPriceError(index, 'price');
                                  }}
                                />
                              ) : (
                                <div className="mt-2 text-sm text-gray-800">
                                  {price.price === '' || price.price === null || price.price === undefined
                                    ? 'Sin precio'
                                    : `$${price.price}`}
                                </div>
                              )}
                              {showEdit && priceErrors?.[index]?.price && (
                                <p className="mt-1 text-xs text-red-600">{priceErrors[index].price}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-600 uppercase">Unidad</label>
                              {showEdit ? (
                                <input
                                  className={`mt-2 border rounded-lg p-2 w-full ${
                                    priceErrors?.[index]?.metricUnit ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  type="text"
                                  disabled={isLoading}
                                  value={price.metricUnit}
                                  onChange={(e) => {
                                    const updatedPrices = [...productPricesInputs];
                                    updatedPrices[index].metricUnit = e.target.value;
                                    setProductPricesInputs(updatedPrices);
                                    clearPriceError(index, 'metricUnit');
                                  }}
                                />
                              ) : (
                                <div className="mt-2 text-sm text-gray-700">{price.metricUnit}</div>
                              )}
                              {showEdit && priceErrors?.[index]?.metricUnit && (
                                <p className="mt-1 text-xs text-red-600">{priceErrors[index].metricUnit}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-600 uppercase">Tipo de cliente</label>
                              {showEdit ? (
                                <select
                                  className={`mt-2 border rounded-lg p-2 w-full ${
                                    priceErrors?.[index]?.clientType ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  value={price.clientType || 'mayorista'}
                                  disabled={isLoading}
                                  onChange={(e) => {
                                    const updatedPrices = [...productPricesInputs];
                                    updatedPrices[index].clientType = e.target.value;
                                    setProductPricesInputs(updatedPrices);
                                    clearPriceError(index, 'clientType');
                                    clearPriceError(index, 'barcode');
                                  }}
                                >
                                  {CLIENT_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div className="mt-2 text-sm text-gray-700">{getClientTypeLabel(price.clientType)}</div>
                              )}
                              {showEdit && priceErrors?.[index]?.clientType && (
                                <p className="mt-1 text-xs text-red-600">{priceErrors[index].clientType}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-600 uppercase">Código de barras</label>
                              {showEdit ? (
                                <input
                                  className={`mt-2 border rounded-lg p-2 w-full ${
                                    priceErrors?.[index]?.barcode ? 'border-red-500' : 'border-gray-300'
                                  }`}
                                  type="text"
                                  value={price.barcode ?? ''}
                                  placeholder="Código de barras"
                                  inputMode="numeric"
                                  maxLength={7}
                                  pattern="[0-9]*"
                                  disabled={isLoading}
                                  onChange={(e) => {
                                    const updatedPrices = [...productPricesInputs];
                                    updatedPrices[index].barcode = sanitizeShortBarcodeInput(e.target.value);
                                    setProductPricesInputs(updatedPrices);
                                    clearPriceError(index, 'barcode');
                                  }}
                                />
                              ) : (
                                <div className="mt-2 text-xs text-gray-600">
                                  {price.barcode ? `Código: ${price.barcode}` : 'Sin código'}
                                </div>
                              )}
                              {showEdit && priceErrors?.[index]?.barcode && (
                                <p className="mt-1 text-xs text-red-600">{priceErrors[index].barcode}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-600 uppercase">Peso desde balanza</label>
                              {showEdit ? (
                                <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(price.useScaleWeight)}
                                    disabled={isLoading}
                                    onChange={(e) => {
                                      const updatedPrices = [...productPricesInputs];
                                      updatedPrices[index].useScaleWeight = e.target.checked;
                                      setProductPricesInputs(updatedPrices);
                                    }}
                                  />
                                  Autocompletar desde balanza
                                </label>
                              ) : (
                                <div className="mt-2 text-sm text-gray-700">
                                  {price.useScaleWeight ? 'Habilitado' : 'Manual'}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 min-w-[150px]">
                            {price.id && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`text-[11px] px-2 py-0.5 rounded-full ${
                                    price.isSoldOut ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                                  }`}
                                >
                                  {price.isSoldOut ? 'Agotado' : 'Disponible'}
                                </span>
                                {canUpdateProduct && (
                                  <button
                                    className={`px-2 py-1 rounded text-[11px] font-semibold ${
                                      price.isSoldOut
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                    onClick={() => handleTogglePriceSoldOut(price.id, price.isSoldOut)}
                                    disabled={priceSoldOutLoading[price.id]}
                                  >
                                    {priceSoldOutLoading[price.id]
                                      ? '...'
                                      : price.isSoldOut
                                        ? 'Marcar disponible'
                                        : 'Marcar agotado'}
                                  </button>
                                )}
                              </div>
                            )}
                            {showEdit && (
                              <div className="flex items-center gap-2">
                                <button
                                  className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2"
                                  onClick={() => handleDeletePrice(price.id, index)}
                                >
                                  <FiTrash2 size={16} />
                                  Quitar
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {showEdit && (
                          <div className="mt-3">
                            <label className="text-xs font-semibold text-gray-600 uppercase">Descripción</label>
                            <input
                              className={`mt-2 border rounded-lg p-2 w-full ${
                                priceErrors?.[index]?.description ? 'border-red-500' : 'border-gray-300'
                              }`}
                              type="text"
                              value={price.description || ''}
                              placeholder="Descripción"
                              maxLength={MAX_VARIETY_DESCRIPTION_LENGTH}
                              disabled={isLoading}
                              onChange={(e) => {
                                const updatedPrices = [...productPricesInputs];
                                updatedPrices[index].description = e.target.value;
                                setProductPricesInputs(updatedPrices);
                                clearPriceError(index, 'description');
                              }}
                            />
                            {priceErrors?.[index]?.description && (
                              <p className="mt-1 text-xs text-red-600">{priceErrors[index].description}</p>
                            )}
                          </div>
                        )}
                        {!showEdit && price.description && (
                          <p className="mt-3 text-sm text-gray-600">{price.description}</p>
                        )}
                        {showEdit && !priceErrors?.[index]?.barcode && barcodeWarning && (
                          <p className="text-xs text-orange-600 mt-2">{barcodeWarning}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <div className="text-xs text-gray-500">
                Los cambios se aplican a precios y códigos de barras.
              </div>
              <div className="flex gap-2">
                {showEdit ? (
                  <>
                    <button
                      className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:text-gray-900"
                      onClick={() => setShowEdit(false)}
                      disabled={isLoading}
                    >
                      Cancelar
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                      onClick={handleUpdateVariety}
                      disabled={isLoading}
                    >
                      Guardar cambios
                    </button>
                  </>
                ) : (
                  <button
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    onClick={onClose}
                  >
                    Cerrar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PriceItem;
